import { api } from '../services/api';
import { invURL } from '../../components/Data/baseURL';
import { normalizePaginatedResponse, PaginatedResponse } from '../utils/pagination';

const LIST_ID = 'LIST' as const;

export interface InvListParams {
  quote?: boolean;
  limit?: number;
  offset?: number;
  search?: string;
  ordering?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface InvItem {
  id?: number | string;
  uf?: string;

  vn?: number | string | null;
  qn?: number | string | null;

  date_inv?: string | null;
  date_due?: string | null;

  is_quote?: boolean;

  bill_to?: Record<string, unknown> | null;

  currency?: Record<string, unknown> | null;

  status?: Record<string, unknown> | null;

  person?: Record<string, unknown> | null;

  payment_term?: Record<string, unknown> | null;

  contract_terms?: Record<string, unknown> | null;

  iteminv_invs?: Array<Record<string, unknown>>;

  comment_invs?: Array<Record<string, unknown>>;

  entry_invs?: Array<Record<string, unknown>>;

  history_invs?: Array<Record<string, unknown>>;

  load_detail?: string | null;
  load_address?: string | null;
  unload_address?: string | null;

  date_load?: string | null;
  date_unload?: string | null;

  [key: string]: unknown;
}

export type InvPayload = Omit<InvItem, 'id'> & {
  id?: number | string;
};

export type PaginatedInvResponse = PaginatedResponse<InvItem>;

interface UpdateInvArgs {
  id: number | string;
  body: InvPayload;
}

export const invApiSlice = api.injectEndpoints({
  endpoints: (builder) => ({
    getInvs: builder.query<PaginatedInvResponse, InvListParams | undefined>({
      query: (params) => ({
        url: invURL,
        method: 'GET',
        params: params ?? undefined,
      }),

      transformResponse: normalizePaginatedResponse<InvItem>,

      providesTags: (result) => [
        {
          type: 'Inv' as const,
          id: LIST_ID,
        },
        ...(result?.results ?? []).map((item) => ({
          type: 'Inv' as const,
          id: item.uf ?? item.id,
        })),
      ],
    }),

    getInv: builder.query<InvItem, number | string>({
      query: (id) => ({
        url: `${invURL}${id}/`,
        method: 'GET',
      }),

      providesTags: (_result, _error, id) => [
        {
          type: 'Inv' as const,
          id,
        },
      ],
    }),

    createInv: builder.mutation<InvItem, InvPayload>({
      query: (body) => ({
        url: invURL + 'create/',
        method: 'POST',
        body,
      }),

      invalidatesTags: [
        {
          type: 'Inv' as const,
          id: LIST_ID,
        },
      ],
    }),

    updateInv: builder.mutation<InvItem, UpdateInvArgs>({
      query: ({ id, body }) => ({
        url: `${invURL}${id}/`,
        method: 'PUT',
        body,
      }),

      invalidatesTags: (_result, _error, { id }) => [
        {
          type: 'Inv' as const,
          id,
        },
        {
          type: 'Inv' as const,
          id: LIST_ID,
        },
      ],
    }),

    deleteInv: builder.mutation<void, number | string>({
      query: (id) => ({
        url: `${invURL}${id}/`,
        method: 'DELETE',
      }),

      invalidatesTags: (_result, _error, id) => [
        {
          type: 'Inv' as const,
          id,
        },
        {
          type: 'Inv' as const,
          id: LIST_ID,
        },
      ],
    }),
  }),
});

export const {
  useGetInvsQuery,
  useLazyGetInvsQuery,
  useGetInvQuery,
  useLazyGetInvQuery,
  useCreateInvMutation,
  useUpdateInvMutation,
  useDeleteInvMutation,
} = invApiSlice;
